import { useRef } from "react";

/**
 * Desktop click-drag horizontal scrolling for an overflow-x container.
 *
 * Touch/pen keep the native scroller untouched; only a mouse button drags —
 * we nudge scrollLeft directly so the same native scroll does the work. The
 * click that *ends* a real drag is swallowed (onClickCapture) so child buttons
 * don't fire on release. Mirrors the carousel drag in KikiCarousel.
 *
 * Usage:
 *   const { ref, dragHandlers } = useDragScroll<HTMLDivElement>();
 *   <div ref={ref} {...dragHandlers} className="overflow-x-auto cursor-grab …">
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const drag = useRef<{ startX: number; startLeft: number } | null>(null);
  const moved = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    // Touch/pen scroll natively — only intercept a mouse button.
    if (e.pointerType !== "mouse") return;
    const el = ref.current;
    if (!el) return;
    drag.current = { startX: e.clientX, startLeft: el.scrollLeft };
    moved.current = false;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const el = ref.current;
    if (!drag.current || !el) return;
    const dx = e.clientX - drag.current.startX;
    // Only treat this as a drag (and capture the pointer) once it actually
    // moves — capturing on pointerdown would retarget the click off a child.
    if (!moved.current && Math.abs(dx) > 4) {
      moved.current = true;
      el.setPointerCapture?.(e.pointerId);
    }
    if (moved.current) el.scrollLeft = drag.current.startLeft - dx;
  };

  const endDrag = () => {
    drag.current = null;
  };

  // Swallow the click that ends a drag so a child doesn't activate on release.
  const onClickCapture = (e: React.MouseEvent) => {
    if (moved.current) {
      e.preventDefault();
      e.stopPropagation();
      moved.current = false;
    }
  };

  return {
    ref,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
    },
  };
}
